import React, { useState, useEffect, useCallback } from 'react';
import { TESTIMONIALS } from '../data/mockData';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../api';

const Testimonials = () => {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const [testimonials, setTestimonials] = useState(TESTIMONIALS);

  useEffect(() => {
    api.getTestimonials().then(data => {
      if (data && data.length > 0) setTestimonials(data);
    }).catch(() => {});
  }, []);

  const next = useCallback(() => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  useEffect(() => {
    const interval = setInterval(next, 6000);
    return () => clearInterval(interval);
  }, [next]);

  const variants = {
    enter: (dir) => ({ x: dir > 0 ? 100 : -100, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -100 : 100, opacity: 0 }),
  };

  return (
    <section className="bg-[#FDFEFA] py-14 md:py-20">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-[#242326] text-3xl md:text-5xl lg:text-[56px] font-light leading-tight tracking-tight font-['Inter'] mb-10 md:mb-14"
        >
          Hear it from our clients
        </motion.h2>

        <div className="relative min-h-[300px] md:min-h-[280px]">
          <AnimatePresence custom={direction} mode="wait">
            <motion.div
              key={current}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.5 }}
              className="w-full"
            >
              <div className="max-w-4xl">
                <Quote className="w-10 h-10 text-[#83BF42]/30 mb-6" />
                <blockquote className="text-[#242326] text-xl md:text-2xl lg:text-[28px] font-light leading-relaxed font-['Inter'] mb-10">
                  {testimonials[current].quote}
                </blockquote>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#83BF42]/10 flex items-center justify-center">
                    <span className="text-[#83BF42] font-bold text-lg font-['Inter']">
                      {testimonials[current].name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-[#242326] font-medium font-['Inter'] text-base">
                      {testimonials[current].name}, {testimonials[current].role}
                    </p>
                    <p className="text-[#B8B8B8] font-['Lato'] text-sm">
                      {testimonials[current].company}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-4 mt-8">
          <button
            onClick={prev}
            className="w-12 h-12 rounded-full border border-[#242326]/15 flex items-center justify-center hover:bg-[#242326] hover:text-white text-[#242326] transition-all duration-300"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={next}
            className="w-12 h-12 rounded-full border border-[#242326]/15 flex items-center justify-center hover:bg-[#242326] hover:text-white text-[#242326] transition-all duration-300"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="flex gap-2 ml-4">
            {testimonials.map((_, i) => (
              <div
                key={i}
                className={`h-1 rounded-full transition-all duration-500 ${
                  i === current
                    ? 'w-8 bg-[#83BF42]'
                    : 'w-3 bg-[#B8B8B8]/40'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
